// src/pages/marketplace/admin/AdminProductDetailPage.tsx

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useProduct, useI18n } from '@react/hooks';
import type { MarketplaceMetadata, ProductStatus as ApiProductStatus } from '@core/types/product';
import {
  Button,
  Textarea,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Alert,
  Badge,
  ErrorAlert,
} from '@react/components';
import { StatusBadge } from '@/components/StatusBadge';
import { Select } from '@/components/Select';

type ProductStatus = 'DRAFT' | 'BETA' | 'ACTIVE' | 'MAINTENANCE' | 'DEPRECATED' | 'DISABLED';

const PRODUCT_STATUSES: ProductStatus[] = [
  'DRAFT',
  'BETA',
  'ACTIVE',
  'MAINTENANCE',
  'DEPRECATED',
  'DISABLED',
];

function mapProductStatusToVariant(
  status: string,
): 'success' | 'warning' | 'error' | 'info' | 'secondary' {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'BETA':
      return 'info';
    case 'DRAFT':
      return 'secondary';
    case 'MAINTENANCE':
      return 'warning';
    case 'DEPRECATED':
      return 'warning';
    case 'DISABLED':
      return 'error';
    default:
      return 'secondary';
  }
}

/** Convert short status names ('DRAFT') to full API enum values ('PRODUCT_STATUS_DRAFT'). */
function toApiProductStatus(status: string): ApiProductStatus {
  return (status.startsWith('PRODUCT_STATUS_')
    ? status
    : `PRODUCT_STATUS_${status}`) as ApiProductStatus;
}

function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '--';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(dateStr);
  }
}

/**
 * Full admin view of a single product.
 * Route: /admin/products/:id
 */
export const AdminProductDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useProduct();
  const { t } = useI18n();
  const item = store.currentItem as any;

  const [statusValue, setStatusValue] = useState<ProductStatus>('ACTIVE');
  const [statusReason, setStatusReason] = useState('');
  const [showStatusForm, setShowStatusForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (id) {
      store.GetProduct(id);
    }
    return () => {
      store.clearCurrent();
    };
  }, [id]);

  useEffect(() => {
    if (item?.status) {
      setStatusValue(item.status);
    }
  }, [item?.status]);

  const handleSetStatus = async () => {
    if (!id) return;
    await store.SetProductStatus(id, {
      status: toApiProductStatus(statusValue),
      reason: statusReason,
    });
    setShowStatusForm(false);
    setStatusReason('');
    store.GetProduct(id);
  };

  const handleVerifyToggle = async () => {
    if (!id) return;
    const isVerified = item?.is_verified || item?.metadata?.is_verified;
    await store.UpdateMarketplaceMetadata(id, {
      marketplace: { is_verified: !isVerified } as MarketplaceMetadata,
    });
    store.GetProduct(id);
  };

  const handleForceDisable = async () => {
    if (!id) return;
    await store.SetProductStatus(id, {
      status: 'PRODUCT_STATUS_DISABLED',
      reason: 'Force disabled by admin',
    });
    store.GetProduct(id);
  };

  const handleDelete = async () => {
    if (!id) return;
    await store.DeleteProduct(id);
    navigate('/admin/products');
  };

  if (store.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (store.error) {
    return (
      <>
        <ErrorAlert error={store.error} />
        <Link to="/admin/products" className="mt-2 inline-block text-sm underline text-primary">
          Back to Products
        </Link>
      </>
    );
  }

  if (!item) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link to="/admin/products" className="mt-2 inline-block text-sm underline text-primary">
          Back to Products
        </Link>
      </Alert>
    );
  }

  const isVerified = item.is_verified || item.metadata?.is_verified;
  const capabilities = Array.isArray(item.capabilities)
    ? item.capabilities
    : item.capabilities
      ? String(item.capabilities).split(',').map((s: string) => s.trim()).filter(Boolean)
      : [];
  const scopes = Array.isArray(item.allowed_scopes)
    ? item.allowed_scopes
    : item.allowed_scopes
      ? String(item.allowed_scopes).split(',').map((s: string) => s.trim()).filter(Boolean)
      : [];
  const redirectUris = Array.isArray(item.redirect_uris)
    ? item.redirect_uris
    : item.redirect_uris
      ? String(item.redirect_uris).split(',').map((s: string) => s.trim()).filter(Boolean)
      : [];
  const grantTypes = Array.isArray(item.allowed_grant_types)
    ? item.allowed_grant_types
    : item.allowed_grant_types
      ? String(item.allowed_grant_types).split(',').map((s: string) => s.trim()).filter(Boolean)
      : [];

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm">
        <Link to="/admin/products" className="text-muted-foreground hover:text-foreground">
          Admin
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/admin/products" className="text-muted-foreground hover:text-foreground">
          Products
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{String(item.name ?? 'Product')}</span>
      </nav>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          {item.icon_url ? (
            <img src={item.icon_url} alt="" className="h-12 w-12 rounded-lg object-cover" />
          ) : (
            <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center">
              <span className="text-lg font-semibold text-muted-foreground">
                {String(item.name ?? '').charAt(0).toUpperCase() || '?'}
              </span>
            </div>
          )}
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground">{String(item.name ?? '')}</h1>
              <StatusBadge
                variant={mapProductStatusToVariant(item.status)}
                label={String(item.status ?? 'UNKNOWN')}
              />
              {isVerified && (
                <Badge variant="default" size="sm">
                  <span className="flex items-center gap-1">
                    <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Verified
                  </span>
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {String(item.slug ?? '')} &middot; Created {formatDate(item.created_at)}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setShowStatusForm(!showStatusForm)}>
            Set Status
          </Button>
          <Button variant="secondary" onClick={handleVerifyToggle}>
            {isVerified ? 'Unverify' : 'Verify'}
          </Button>
          <Button variant="danger" onClick={handleForceDisable}>
            Disable
          </Button>
          <Button
            variant="danger"
            onClick={() => setShowDeleteConfirm(true)}
          >
            Delete
          </Button>
        </div>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Basic Info */}
      <Card>
        <CardHeader>
          <CardTitle>Basic Info</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Slug</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.slug ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Name</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.name ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Description</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.description ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Tagline</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.tagline ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Owner</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String(item.owner?.name || item.owner || '--')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Legal Entity</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.legal_entity ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Built-in</dt>
              <dd className="text-sm text-foreground col-span-2">
                {item.is_builtin ? 'Yes' : 'No'}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Public</dt>
              <dd className="text-sm text-foreground col-span-2">
                {item.is_public ? 'Yes' : 'No'}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* URLs */}
      <Card>
        <CardHeader>
          <CardTitle>URLs</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            {[
              ['Web URL', item.web_url],
              ['API Base URL', item.api_base_url],
              ['Docs URL', item.docs_url],
              ['Support URL', item.support_url],
            ].map(([label, value]) => (
              <div key={String(label)} className="py-3 grid grid-cols-3 gap-4">
                <dt className="text-sm font-medium text-muted-foreground">{String(label)}</dt>
                <dd className="text-sm text-foreground col-span-2">
                  {value ? (
                    <a
                      href={String(value)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {String(value)}
                    </a>
                  ) : (
                    '--'
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>

      {/* OAuth2 */}
      <Card>
        <CardHeader>
          <CardTitle>OAuth2</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Client ID</dt>
              <dd className="text-sm text-foreground col-span-2 font-mono">
                {String(item.oauth2_client_id ?? '--')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Scopes</dt>
              <dd className="text-sm col-span-2">
                {scopes.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {scopes.map((s: string, i: number) => (
                      <Badge key={i} variant="secondary" size="sm">
                        {s}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <span className="text-muted-foreground">--</span>
                )}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Redirect URIs</dt>
              <dd className="text-sm col-span-2">
                {redirectUris.length > 0 ? (
                  <ul className="space-y-1">
                    {redirectUris.map((uri: string, i: number) => (
                      <li key={i} className="font-mono text-foreground text-xs">
                        {uri}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="text-muted-foreground">--</span>
                )}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Grant Types</dt>
              <dd className="text-sm col-span-2">
                {grantTypes.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {grantTypes.map((g: string, i: number) => (
                      <Badge key={i} variant="secondary" size="sm">
                        {g}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <span className="text-muted-foreground">--</span>
                )}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Capabilities */}
      <Card>
        <CardHeader>
          <CardTitle>Capabilities</CardTitle>
        </CardHeader>
        <CardContent>
          {capabilities.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {capabilities.map((cap: string, i: number) => (
                <Badge key={i} variant="primary" size="sm">
                  {cap}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No capabilities defined</p>
          )}
        </CardContent>
      </Card>

      {/* Marketplace Stats */}
      <Card>
        <CardHeader>
          <CardTitle>Marketplace Stats</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-muted-foreground">Average Rating</p>
              <p className="text-2xl font-bold text-foreground">
                {item.average_rating ?? item.metadata?.average_rating ?? '--'}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Reviews</p>
              <p className="text-2xl font-bold text-foreground">
                {item.review_count ?? item.metadata?.review_count ?? 0}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Installs</p>
              <p className="text-2xl font-bold text-foreground">
                {item.install_count ?? item.metadata?.install_count ?? 0}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Active Installs</p>
              <p className="text-2xl font-bold text-foreground">
                {item.active_installs ?? item.metadata?.active_installs ?? 0}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Status History / Change Status */}
      <Card>
        <CardHeader>
          <CardTitle>Change Status</CardTitle>
          <CardDescription>
            Update the product status. Provide a reason for auditing purposes.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">New Status</label>
            <Select
              value={statusValue}
              onChange={(e) => setStatusValue(e.target.value as ProductStatus)} className="w-full max-w-xs">
              {PRODUCT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s.charAt(0) + s.slice(1).toLowerCase()}
                </option>
              ))}
            </Select>
          </div>
          <Textarea
            label="Reason"
            placeholder="Provide a reason for the status change..."
            value={statusReason}
            onChange={(e) => setStatusReason(e.target.value)}
            rows={3}
          />
        </CardContent>
        <CardFooter className="flex justify-end">
          <Button onClick={handleSetStatus}>Update Status</Button>
        </CardFooter>
      </Card>

      {/* Admin Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Admin Actions</CardTitle>
          <CardDescription>
            Destructive actions. Use with caution.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-border">
            <div>
              <p className="text-sm font-medium text-foreground">Verify Product</p>
              <p className="text-xs text-muted-foreground">
                {isVerified
                  ? 'This product is verified. Click to revoke verification.'
                  : 'Mark this product as verified by the platform team.'}
              </p>
            </div>
            <Button
              variant={isVerified ? 'secondary' : 'primary'}
              size="sm"
              onClick={handleVerifyToggle}
            >
              {isVerified ? 'Revoke Verification' : 'Verify Product'}
            </Button>
          </div>

          <div className="flex items-center justify-between py-3 border-b border-border">
            <div>
              <p className="text-sm font-medium text-foreground">Force Disable</p>
              <p className="text-xs text-muted-foreground">
                Immediately disable this product for all tenants.
              </p>
            </div>
            <Button variant="danger" size="sm" onClick={handleForceDisable}>
              Force Disable
            </Button>
          </div>

          <div className="flex items-center justify-between py-3">
            <div>
              <p className="text-sm font-medium text-destructive">Delete Product</p>
              <p className="text-xs text-muted-foreground">
                Permanently remove this product. This cannot be undone.
              </p>
            </div>
            <Button
              variant="danger"
              size="sm"
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete Product
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Delete confirmation dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="text-destructive">Delete Product</CardTitle>
              <CardDescription>
                Are you sure you want to permanently delete &quot;{String(item.name ?? '')}&quot;?
                This action cannot be undone. All associated data will be removed.
              </CardDescription>
            </CardHeader>
            <CardFooter className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleDelete}>
                Delete Permanently
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}
    </div>
  );
});

export default AdminProductDetailPage;
